import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { Task } from '~/modules/task/types';

interface TaskInteractionState {
  /** Ids of tasks selected on the board or table; entities resolve from the query cache at action time. */
  selectedTaskIds: string[];
  /** Project of the selected tasks, null while none are: a selection stays within one project, so bulk pickers have one set of labels and members. */
  selectedProjectId: string | null;
  setSelectedTaskIds: (ids: string[], projectId?: string) => void;
  /**
   * Ids of label rows selected in the labels panel. Task and label selection are mutually exclusive
   * (checkboxes of the other kind disable), so the board header's remove button acts on one kind.
   */
  selectedLabelIds: string[];
  setSelectedLabelIds: (ids: string[]) => void;
  /**
   * Draft tasks keyed by projectId for inline create-form placeholders. A project's create form
   * is open iff it has a draft here, so consumers derive open-ness from `draftTasks[projectId]`.
   */
  draftTasks: Record<string, Task>;
  setDraftTask: (projectId: string, task: Task | null) => void;
  updateDraftTask: (projectId: string, updates: Partial<Task>) => void;
  /** Reset all interaction state. Call when leaving a workspace/board context. */
  reset: () => void;
}

const initialState: Pick<TaskInteractionState, 'selectedTaskIds' | 'selectedProjectId' | 'selectedLabelIds' | 'draftTasks'> = {
  selectedTaskIds: [],
  selectedProjectId: null,
  selectedLabelIds: [],
  draftTasks: {},
};

/**
 * Store to manage task interactions such as selection and open create forms across the task board. The current item lives in `useBoardFocusStore`.
 * This allows for consistent state management and avoids prop drilling for these common interactions.
 */
export const useTaskInteractionStore = create<TaskInteractionState>()(
  devtools(
    immer((set) => ({
      ...initialState,
      setSelectedTaskIds: (ids, projectId) => {
        set((state) => {
          state.selectedTaskIds = ids;
          state.selectedProjectId = ids.length ? (projectId ?? state.selectedProjectId) : null;
        });
      },
      setSelectedLabelIds: (ids) => {
        set((state) => {
          state.selectedLabelIds = ids;
        });
      },
      setDraftTask: (projectId, task) => {
        set((state) => {
          if (task) state.draftTasks[projectId] = task;
          else delete state.draftTasks[projectId];
        });
      },
      updateDraftTask: (projectId, updates) => {
        set((state) => {
          const draft = state.draftTasks[projectId];
          if (draft) Object.assign(draft, updates);
        });
      },
      reset: () => {
        set((state) => {
          Object.assign(state, initialState);
        });
      },
    })),
  ),
);
